import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/stores/authStore'
import { useTheme } from '@/contexts/ThemeContext'
import { Sun, Moon, Eye, EyeOff } from 'lucide-react'
import axios from 'axios'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080'

export function LoginScreen() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [registrationEnabled, setRegistrationEnabled] = useState(true)
  const navigate = useNavigate()
  const { isDark, toggleTheme } = useTheme()

  useEffect(() => {
    axios.get(`${API_URL}/auth/public/settings`)
      .then(res => setRegistrationEnabled(res.data.registration_enabled))
      .catch(() => setRegistrationEnabled(true))
  }, [])

  const { login, isLoading, error, clearError } = useAuthStore()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    clearError()

    try {
      await login(email, password)
      navigate('/chat')
    } catch (error) {
      // Error já tratado no store
    }
  }

  return (
    <div className="min-h-screen bg-brand-bg flex flex-col items-center justify-center px-4 transition-colors duration-300">
      {/* Theme Toggle */}
      <button
        onClick={toggleTheme}
        className="absolute top-4 right-4 p-2.5 rounded-xl text-brand-text-secondary hover:bg-brand-surface-alt transition-all duration-200"
        aria-label={isDark ? 'Ativar modo claro' : 'Ativar modo escuro'}
      >
        {isDark ? <Sun size={22} /> : <Moon size={22} />}
      </button>

      <div className="max-w-md w-full card p-8 shadow-sm">
        {/* Logo */}
        <div className="text-center mb-8">
          <img
            src={isDark ? '/assets/logos/Logotipo-Verde-Broto.svg' : '/assets/logos/Logotipo-Verde.svg'}
            alt="RegenerAI"
            className="h-28 mx-auto mb-6"
          />
          <p className="text-brand-text-secondary text-sm">
            Plataforma de Agricultura Regenerativa
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-900/30 rounded-xl">
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-brand-text-secondary mb-2">
              Email
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input-field"
              placeholder="seu@email.com"
              required
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label htmlFor="password" className="block text-sm font-medium text-brand-text-secondary">
                Senha
              </label>
              <Link
                to="/forgot-password"
                className="text-xs text-brand-primary hover:text-brand-primary-hover font-medium transition-colors"
              >
                Esqueci minha senha
              </Link>
            </div>
            <div className="relative">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => { setPassword(e.target.value); clearError() }}
                className="input-field pr-11"
                placeholder="••••••••"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-brand-text-muted hover:text-brand-text transition-colors"
                tabIndex={-1}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full btn-primary py-3 text-center disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <span className="flex items-center justify-center gap-2">
                <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Entrando...
              </span>
            ) : 'Entrar'}
          </button>
        </form>

        {/* Links */}
        {registrationEnabled && (
          <div className="mt-6 text-center">
            <p className="text-sm text-brand-text-muted">
              Não tem conta?{' '}
              <Link to="/register" className="text-brand-primary font-medium hover:text-brand-primary-hover transition-colors">
                Cadastre-se
              </Link>
            </p>
          </div>
        )}
      </div>
    </div>
  )
}